/**
 * MasterImageTabs
 *
 * Horizontal tab navigation for Master Image Generation modes.
 * Navigates between /studio/single-shot and /studio/variations.
 */

import { useNavigate, useLocation } from 'react-router-dom';
import { Zap, Dices } from 'lucide-react';

export type TabId = 'single-shot' | 'variations';

interface MasterImageTabsProps {
  activeTab: TabId;
  onTabChange: (tabId: TabId) => void;
}

const TABS: Array<{ id: TabId; label: string; icon: React.ElementType; path: string }> = [
  { id: 'single-shot', label: 'Single Shot', icon: Zap, path: '/v2/tools/studio/single-shot' },
  { id: 'variations', label: 'Variationen', icon: Dices, path: '/v2/tools/studio/variations' },
];

export function MasterImageTabs({ activeTab }: MasterImageTabsProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const currentTab = TABS.find(t => location.pathname.includes(t.id))?.id ?? activeTab;

  return (
    <div className="border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900">
      <div className="flex items-center gap-1 px-3 md:px-6">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = currentTab === tab.id;

          return (
            <button
              key={tab.id}
              onClick={() => navigate(tab.path)}
              className={`
                flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors
                border-b-2 -mb-px
                ${
                  isActive
                    ? 'border-orange-500 text-orange-600 dark:text-orange-400'
                    : 'border-transparent text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100'
                }
              `}
              aria-current={isActive ? 'page' : undefined}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
